// QAPAQ SpA — Contenido de las páginas públicas de la Fase 3 SEO
//
// Cada página se renderiza con <PaginaInfo> y se prerenderiza en el build
// (scripts/prerender.js), así que el texto llega en el HTML para Google y los
// buscadores de IA. Lo que se afirma aquí tiene que calzar con los T&C vigentes
// (src/lib/terminos.js) y con el modelo de cobro real: el flete se paga directo
// entre oferente y transportista; QAPAQ cobra una comisión a cada parte; la guía
// de despacho la emite el oferente. No se publican porcentajes de comisión:
// cambian por plan y son un compromiso comercial que decide el dueño.

export const PAGINAS = {
  transportistas: {
    ruta: '/transportistas',
    titulo: 'Cargas para transportistas en todo Chile — QAPAQ',
    descripcion: 'Encuentra cargas en tu ruta, incluidas las de retorno, en las 16 regiones de Chile. Cotiza, coordina por chat y cobra el flete directo al oferente.',
    kicker: 'Para transportistas',
    h1: 'Cargas para tu camión en todo Chile',
    intro: 'QAPAQ reúne las cargas que publican empresas y personas a lo largo del país. Eliges las que calzan con tu ruta y tu vehículo, cotizas y coordinas directo con quien envía.',
    secciones: [
      {
        titulo: 'Cómo encuentras carga',
        items: [
          'Filtra las cargas por origen, destino y tipo de vehículo requerido.',
          'Recibe alertas de remates en las regiones donde trabajas.',
          'Aprovecha tus viajes de vuelta con cargas de retorno y evita volver vacío.',
          'Antes de cotizar, revisa el Score de Confiabilidad del oferente: muestra qué tan exacto declara el peso, el volumen y el tipo de su carga.',
        ],
      },
      {
        titulo: 'Dos formas de trabajar',
        items: [
          'Remate: el oferente publica y los transportistas ofertan. La carga se adjudica en minutos.',
          'Planificado: cargas con fecha programada que puedes cotizar con tiempo y ordenar tu semana.',
        ],
      },
      {
        titulo: 'Cómo cobras',
        parrafos: [
          'El precio del flete lo acuerdas con el oferente y él te lo paga directamente a ti: QAPAQ no recauda ni retiene tu flete.',
          'QAPAQ cobra una comisión por la gestión de la carga y el acceso a la plataforma. Su valor vigente se informa en la plataforma y en los Términos y Condiciones.',
        ],
      },
      {
        titulo: 'Qué necesitas para operar',
        parrafos: ['Para trabajar en QAPAQ debes acreditar y mantener vigentes:'],
        items: [
          'Licencia de conducir de la clase que corresponde a tu vehículo.',
          'Revisión técnica al día.',
          'SOAP vigente.',
          'Certificado de antecedentes comerciales.',
          'Seguro de carga vigente.',
        ],
        nota: 'La plataforma te avisa 30 días antes de que venza cualquiera de estos documentos.',
      },
      {
        titulo: 'Tu reputación trabaja por ti',
        parrafos: [
          'Cada entrega suma a tu Score de transportista, que mide puntualidad, estado de la carga, comunicación y apego a la ruta. Los oferentes lo ven al momento de elegir a quién adjudicar.',
          'Si tienes más de un camión, administra tu flota, tus choferes y sus documentos, y sigue cada viaje por GPS desde la misma cuenta.',
        ],
      },
    ],
    cta: { texto: 'Registrar mi camión', ruta: '/app/registro' },
  },

  empresas: {
    ruta: '/empresas',
    titulo: 'Cotiza fletes y transporte de carga en Chile — QAPAQ',
    descripcion: 'Publica tu carga y recibe cotizaciones de transportistas con documentos acreditados. Compara precio y reputación y sigue tu envío por GPS en todo Chile.',
    kicker: 'Para empresas y personas',
    h1: 'Cotiza tu flete y recibe ofertas en minutos',
    intro: 'Desde pymes hasta empresas grandes, y también personas: publicas tu carga una vez y los transportistas de la red te cotizan. Tú comparas y eliges.',
    secciones: [
      {
        titulo: 'Cómo publicas tu carga',
        items: [
          'Indica origen, destino, tipo de carga, peso y volumen.',
          'Elige la modalidad: remate, para recibir ofertas rápidas, o planificado, para un viaje con fecha programada.',
          'Declara el valor comercial de la carga: define hasta qué monto responde el transportista si ocurre un siniestro.',
        ],
      },
      {
        titulo: 'Cómo eliges al transportista',
        items: [
          'Compara el precio de cada cotización.',
          'Revisa el Score del transportista: su historial de puntualidad, estado de la carga y comunicación.',
          'Todos los transportistas deben acreditar licencia, revisión técnica, SOAP y seguro de carga vigentes para operar.',
        ],
      },
      {
        titulo: 'Durante el viaje',
        items: [
          'Sigue tu envío por GPS durante el trayecto.',
          'Coordina por chat directo con el transportista.',
          'Confirma la entrega con un código QR de un solo uso.',
          'Guarda el historial completo de tus cargas.',
        ],
      },
      {
        titulo: 'Cuánto cuesta',
        parrafos: [
          'Crear tu cuenta y publicar no tiene costo. Cuando adjudicas un flete, QAPAQ cobra una comisión por la intermediación; si envías seguido, hay planes mensuales con comisiones menores.',
          'El flete se lo pagas directamente al transportista. La guía de despacho de tu carga la emites tú, como oferente; en QAPAQ puedes registrar su número para que quede asociada al envío.',
        ],
      },
      {
        titulo: 'Cargas que no se pueden publicar',
        parrafos: ['Por seguridad y normativa, QAPAQ no admite, entre otras:'],
        items: [
          'Valores y objetos preciosos.',
          'Seres vivos, restos y material biológico.',
          'Sustancias controladas y armas.',
          'Alimentos que requieren cadena de frío.',
          'Residuos y desechos.',
          'Bienes regulados y cargas especiales.',
        ],
        nota: 'El detalle completo está en los Términos y Condiciones.',
      },
    ],
    cta: { texto: 'Publicar mi primera carga', ruta: '/app/registro' },
  },

  'como-funciona': {
    ruta: '/como-funciona',
    titulo: 'Cómo funciona QAPAQ y preguntas frecuentes — QAPAQ',
    descripcion: 'Paso a paso para publicar una carga, recibir cotizaciones, seguir el viaje y confirmar la entrega con QR. Preguntas frecuentes sobre costos, pagos y documentos.',
    kicker: 'Paso a paso',
    h1: 'Cómo funciona QAPAQ',
    intro: 'QAPAQ es una plataforma chilena que conecta a quienes necesitan mover carga con transportistas en las 16 regiones del país. El contrato de transporte es entre ellos; QAPAQ facilita el encuentro, la coordinación y la trazabilidad.',
    pasos: [
      { titulo: 'Crea tu cuenta', texto: 'Como empresa o persona que envía carga, o como transportista. Registrarse no tiene costo.' },
      { titulo: 'Publica la carga', texto: 'Origen, destino, tipo, peso, volumen y valor declarado. Eliges remate o planificado.' },
      { titulo: 'Recibe cotizaciones', texto: 'Los transportistas cotizan. Comparas precio y reputación y adjudicas.' },
      { titulo: 'Coordina y sigue el viaje', texto: 'Chat directo con la otra parte y seguimiento GPS durante el trayecto.' },
      { titulo: 'Confirma la entrega', texto: 'El transportista escanea el código QR que muestra quien recibe la carga.' },
      { titulo: 'Califica', texto: 'Cada entrega alimenta el Score del transportista y el del oferente.' },
    ],
    faq: [
      {
        p: '¿Qué es QAPAQ?',
        r: 'Una plataforma chilena que conecta a empresas y personas que necesitan mover carga con transportistas de todo el país. QAPAQ no es el transportista: el contrato de transporte se celebra directamente entre el oferente de la carga y el transportista.',
      },
      {
        p: '¿Cuánto cuesta usar QAPAQ?',
        r: 'Crear una cuenta y publicar no tiene costo. QAPAQ cobra una comisión por intermediación al oferente y al transportista cuando se adjudica un flete. Para oferentes que envían seguido hay planes mensuales con comisiones menores. Los valores vigentes se informan en la plataforma y en los Términos y Condiciones.',
      },
      {
        p: '¿Cómo se paga el flete?',
        r: 'El flete se paga directamente entre el oferente y el transportista. QAPAQ no recauda ni retiene el pago del flete; solo cobra su comisión.',
      },
      {
        p: '¿Quién emite la guía de despacho?',
        r: 'El oferente de la carga, como corresponde según la normativa del SII. En QAPAQ puede registrar el número de su guía para que quede asociada al envío.',
      },
      {
        p: '¿Cómo sé que un transportista es confiable?',
        r: 'Para operar, todo transportista debe acreditar licencia de conducir de la clase correspondiente, revisión técnica, SOAP, certificado de antecedentes comerciales y seguro de carga vigentes. Además, cada uno tiene un Score que refleja su historial de puntualidad, estado de la carga, comunicación y apego a la ruta.',
      },
      {
        p: '¿Qué pasa si la carga llega dañada?',
        r: 'Al recibir, deja constancia de cualquier daño o faltante antes de dar conformidad. Luego presenta el reclamo en el módulo de disputas dentro de las 48 horas hábiles siguientes, con fotos, una descripción y el documento que acredite el valor de la carga. El responsable de la carga durante el transporte es el transportista, que debe tener seguro de carga vigente; el valor que declaraste al publicar define hasta qué monto responde.',
      },
      {
        p: '¿Puedo cancelar después de adjudicar?',
        r: 'Sí, sin penalidad, dentro de la ventana de gracia: 10 minutos en modalidad remate y 2 horas en modalidad planificada. Después de ese plazo, la cancelación descuenta puntos del Score de quien cancela.',
      },
      {
        p: '¿Qué cargas no se pueden transportar?',
        r: 'Entre otras, valores y objetos preciosos, seres vivos y material biológico, sustancias controladas y armas, alimentos que requieren cadena de frío, residuos y bienes regulados. El listado completo está en los Términos y Condiciones.',
      },
      {
        p: '¿En qué regiones funciona?',
        r: 'En las 16 regiones de Chile. QAPAQ opera desde Hijuelas, en la Región de Valparaíso.',
      },
      {
        p: '¿Qué es el Score de Confiabilidad del oferente?',
        r: 'Una nota de 1,0 a 5,0 que calculan las evaluaciones de los transportistas: mide si el oferente declara bien el peso, el tipo y el volumen de la carga, si la tiene lista a la hora acordada y si hay alguien para recibirla. Los transportistas la ven antes de cotizar.',
      },
    ],
    cta: { texto: 'Crear cuenta gratis', ruta: '/app/registro' },
  },

  'quienes-somos': {
    ruta: '/quienes-somos',
    titulo: 'Quiénes somos — QAPAQ, marketplace de carga chileno',
    descripcion: 'QAPAQ es un marketplace de carga creado en Hijuelas, Región de Valparaíso, para conectar a quienes envían carga con transportistas de todo Chile.',
    kicker: 'Quiénes somos',
    h1: 'Una idea de años, hecha plataforma',
    intro: 'En Chile, mover carga todavía depende de llamadas, contactos y camiones que vuelven vacíos. QAPAQ nació para ordenar ese encuentro entre quien necesita enviar y quien puede transportar.',
    secciones: [
      {
        titulo: 'Por qué existe QAPAQ',
        parrafos: [
          'La idea de una plataforma así se conversó durante años. Siempre chocaba con lo mismo: construirla exigía un equipo de desarrollo que no estaba a nuestro alcance, y quedaba en conversación.',
          'La inteligencia artificial cambió eso. Las barreras para programar bajaron, y quien conoce el negocio hoy puede diseñar y construir su propio software. Así nació QAPAQ: una plataforma construida con herramientas de IA, desde Chile y para el transporte chileno.',
          'La IA nos ayuda a construir y operar, pero las decisiones que importan —qué se cobra, qué se le exige a cada transportista, cómo se cuidan los datos— las toman personas.',
        ],
      },
      {
        titulo: 'Quién está detrás',
        parrafos: [
          'Alejandro Zapata Verdugo — Fundador.',
          'QAPAQ SpA, RUT 78.415.721-0.',
        ],
      },
      {
        titulo: 'Dónde estamos',
        parrafos: [
          'Operamos desde Hijuelas, en la Región de Valparaíso. La plataforma funciona en las 16 regiones de Chile.',
        ],
      },
      {
        titulo: 'Cómo trabajamos',
        items: [
          'Somos intermediarios: conectamos a quien envía con quien transporta y cobramos una comisión. El contrato de transporte es entre ellos.',
          'Reputación a la vista: cada entrega alimenta el Score del transportista y el del oferente, y ambas partes pueden verlos antes de cerrar un trato.',
          'Reglas claras: requisitos, plazos y cargas permitidas están en nuestros Términos y Condiciones.',
        ],
      },
      {
        titulo: 'Contacto',
        items: [
          'Correo: contacto@qapaq.cl',
          'Instagram: @qapaq.cl',
          'Facebook: QAPAQ SpA',
        ],
      },
    ],
    cta: { texto: 'Crear cuenta gratis', ruta: '/app/registro' },
  },
}

export const LISTA_PAGINAS = Object.entries(PAGINAS).map(([slug, p]) => ({ slug, ...p }))
